import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Trophy, Target, Flame, Download, Monitor, RefreshCw, QrCode, ChevronRight, Eye, Check, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

const API = '/api';

export default function Leaderboard() {
  const { matchId: urlMatchId } = useParams();
  const [searchParams] = useSearchParams();
  const isDisplayMode = searchParams.get("display") === "true";
  
  const [matches, setMatches] = useState([]);
  const [selectedMatch, setSelectedMatch] = useState(urlMatchId || "");
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(isDisplayMode);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [scorecardDialogOpen, setScorecardDialogOpen] = useState(false);
  const [selectedScorecardShooter, setSelectedScorecardShooter] = useState(null);
  const [scorecardData, setScorecardData] = useState(null);
  const [loadingScorecard, setLoadingScorecard] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    fetchMatches();
  }, []);

  useEffect(() => {
    if (selectedMatch) {
      fetchLeaderboard();
    } else {
      setLeaderboardData(null);
    }
  }, [selectedMatch]);

  // Auto-refresh for display mode
  useEffect(() => {
    let interval;
    if (autoRefresh && selectedMatch) {
      interval = setInterval(() => {
        fetchLeaderboard();
      }, 10000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh, selectedMatch]);

  const fetchMatches = async () => {
    try {
      const res = await axios.get(`${API}/matches`);
      setMatches(res.data);
      if (urlMatchId) {
        setSelectedMatch(urlMatchId);
      }
    } catch (error) {
      toast.error("Failed to fetch matches");
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard/${selectedMatch}`);
      setLeaderboardData(res.data);
    } catch (error) {
      toast.error("Failed to fetch leaderboard");
    }
  };

  const getSelectedMatchData = () => {
    return matches.find(m => m.id === selectedMatch);
  };

  const getRankClass = (rank) => {
    if (rank === 1) return "rank-gold";
    if (rank === 2) return "rank-silver";
    if (rank === 3) return "rank-bronze";
    return "";
  };

  const getRankBadge = (rank) => {
    return rank;
    return rank;
  };

  const handleExportPDF = () => {
    const matchData = getSelectedMatchData();
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${matchData?.name || "Leaderboard"} - NMLRA</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: 'Georgia', serif; padding: 40px; color: #1e293b; }
            .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 3px solid #1B3B6F; }
            .header h1 { font-size: 28px; color: #1B3B6F; margin-bottom: 8px; }
            .header h2 { font-size: 20px; font-weight: normal; color: #475569; }
            .header p { font-size: 14px; color: #64748b; margin-top: 8px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th { background: #1B3B6F; color: white; padding: 12px 16px; text-align: left; font-size: 12px; text-transform: uppercase; }
            td { padding: 12px 16px; border-bottom: 1px solid #e2e8f0; }
            tr:nth-child(even) { background: #f8fafc; }
            .rank { font-size: 18px; font-weight: bold; text-align: center; }
            .score { font-family: 'Courier New', monospace; font-size: 18px; font-weight: bold; color: #15803d; }
            .streak { font-family: 'Courier New', monospace; color: #ea580c; }
            .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #94a3b8; }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>National Muzzle Loading Rifle Association</h1>
            <h2>${matchData?.name || "Match Leaderboard"}</h2>
            <p>${leaderboardData?.shots_per_round || 25} shots per round</p>
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 60px;">Rank</th>
                <th>Shooter</th>
                <th style="text-align: center;">Best Score</th>
                <th style="text-align: center;">Best Streak</th>
                <th style="text-align: center;">Rounds</th>
              </tr>
            </thead>
            <tbody>
              ${leaderboardData?.leaderboard?.map((entry) => `
                <tr>
                  <td class="rank">${entry.rank}</td>
                  <td>${entry.shooter_name}</td>
                  <td style="text-align: center;"><span class="score">${entry.best_score}</span>/${leaderboardData?.shots_per_round || 25}</td>
                  <td style="text-align: center;" class="streak">${entry.best_streak}</td>
                  <td style="text-align: center;">${entry.total_rounds}</td>
                </tr>
              `).join("") || "<tr><td colspan='5'>No scores yet</td></tr>"}
            </tbody>
          </table>
          <div class="footer">Generated on ${new Date().toLocaleString()} | NMLRA Trap Tracker</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const openDisplayMode = () => {
    const displayUrl = `${window.location.origin}/leaderboard/${selectedMatch}?display=true`;
    window.open(displayUrl, "_blank", "width=1920,height=1080");
    toast.success("Display window opened!");
  };

  const copyMobileLink = () => {
    const mobileUrl = `${window.location.origin}/scores/${selectedMatch}`;
    navigator.clipboard.writeText(mobileUrl);
    toast.success("Mobile scores link copied!");
  };

  const getMobileUrl = () => `${window.location.origin}/scores/${selectedMatch}`;

  const viewScorecard = async (entry) => {
    setSelectedScorecardShooter(entry);
    setScorecardDialogOpen(true);
    setLoadingScorecard(true);
    
    try {
      // Fetch all rounds for this shooter in this match
      const res = await axios.get(`${API}/rounds?entry_id=${entry.id}`);
      // Filter to only show the best round (is_best_round = true) or the highest score
      const allRounds = res.data;
      let bestRound = allRounds.find(r => r.is_best_round);
      if (!bestRound && allRounds.length > 0) {
        // Fallback: find the round with the highest score
        bestRound = allRounds.reduce((best, current) => {
          const bestScore = best.score || best.total_hits || 0;
          const currentScore = current.score || current.total_hits || 0;
          return currentScore > bestScore ? current : best;
        }, allRounds[0]);
      }
      setScorecardData(bestRound ? [bestRound] : []);
    } catch (error) {
      toast.error("Failed to load scorecard");
      setScorecardData([]);
    } finally {
      setLoadingScorecard(false);
    }
  };

  const calculateLongestRun = (scoresArray) => {
    if (!scoresArray || !Array.isArray(scoresArray)) return 0;
    let maxRun = 0;
    let currentRun = 0;
    for (const score of scoresArray) {
      if (score === true || score === 1) {
        currentRun++;
        maxRun = Math.max(maxRun, currentRun);
      } else {
        currentRun = 0;
      }
    }
    return maxRun;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="leaderboard-loading">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  // Display Mode
  if (isDisplayMode) {
    const matchData = getSelectedMatchData();
    return (
      <div className="min-h-screen bg-[#1B3B6F] p-8" data-testid="leaderboard-display-mode">
        <div className="text-center mb-8">
          <h1 className="text-white font-serif text-4xl font-bold tracking-tight">
            NMLRA Trap Tracker
          </h1>
          <h2 className="text-white/90 text-3xl mt-2 font-semibold">
            {matchData?.name || "Match Leaderboard"}
          </h2>
          <div className="flex items-center justify-center gap-2 mt-2 text-white/60">
            <RefreshCw size={14} className={autoRefresh ? "animate-spin" : ""} />
            <span className="text-sm">Auto-refreshing every 10s</span>
          </div>
        </div>

        <div className="max-w-5xl mx-auto bg-white rounded-xl shadow-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-800 text-white">
                <th className="py-4 px-6 text-left text-lg">Rank</th>
                <th className="py-4 px-6 text-left text-lg">Shooter</th>
                <th className="py-4 px-6 text-center text-lg">Score</th>
                <th className="py-4 px-6 text-center text-lg">
                  <Flame className="inline mr-1" size={18} /> Streak
                </th>
                <th className="py-4 px-6 text-center text-lg">Rounds</th>
              </tr>
            </thead>
            <tbody>
              {leaderboardData?.leaderboard?.slice(0, 15).map((entry, idx) => (
                <tr key={entry.shooter_id || idx} className={`${idx % 2 === 0 ? "bg-white" : "bg-slate-50"} ${entry.rank <= 3 ? getRankClass(entry.rank) : ""}`}>
                  <td className="py-5 px-6 text-3xl font-bold text-center">{getRankBadge(entry.rank)}</td>
                  <td className="py-5 px-6 text-xl font-medium">{entry.shooter_name}</td>
                  <td className="py-5 px-6 text-center">
                    <span className="font-mono text-3xl font-bold text-green-700">{entry.best_score}</span>
                    <span className="text-slate-400 text-lg">/{leaderboardData?.shots_per_round || 25}</span>
                  </td>
                  <td className="py-5 px-6 text-center font-mono text-2xl text-orange-600 font-bold">{entry.best_streak}</td>
                  <td className="py-5 px-6 text-center font-mono text-xl text-slate-500">{entry.total_rounds}</td>
                </tr>
              ))}
              {(!leaderboardData?.leaderboard || leaderboardData.leaderboard.length === 0) && (
                <tr><td colSpan={5} className="py-12 text-center text-slate-400 text-xl">No scores recorded yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="text-center mt-8 text-white/40 text-sm">
          National Muzzle Loading Rifle Association | {new Date().toLocaleDateString()}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="leaderboard-page" className="space-y-6" ref={printRef}>
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
          Leaderboard
        </h1>
        <p className="text-slate-500 mt-1">Select a match to view rankings</p>
      </div>

      {/* Match Selection - Card List */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {matches.map((match) => (
          <Card 
            key={match.id} 
            className={`cursor-pointer transition-all hover:shadow-lg ${selectedMatch === match.id ? 'ring-2 ring-[#1B3B6F] bg-blue-50' : 'hover:bg-slate-50'}`}
            onClick={() => setSelectedMatch(match.id)}
            data-testid={`match-card-${match.id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg text-slate-800">{match.name}</h3>
                  <p className="text-sm text-slate-500">
                    {match.shots_per_round || 25} shots/round
                    {match.is_protection && " • Team Match"}
                  </p>
                  {match.match_number && (
                    <span className="text-xs text-slate-400 font-mono">#{match.match_number}</span>
                  )}
                </div>
                <ChevronRight className={`text-slate-400 ${selectedMatch === match.id ? 'text-[#1B3B6F]' : ''}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {matches.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">No matches found. Create matches first.</p>
          </CardContent>
        </Card>
      )}

      {/* Selected Match Leaderboard */}
      {selectedMatch && leaderboardData && (
        <>
          {/* Match Header */}
          <Card className="hero-gradient text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                  <Trophy size={40} className="text-amber-300" />
                  <div>
                    <h2 className="font-serif text-2xl font-bold">
                      {getSelectedMatchData()?.name}
                    </h2>
                    <p className="text-white/80 font-mono text-sm">
                      {leaderboardData.shots_per_round} shots per round
                    </p>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="secondary" onClick={handleExportPDF}>
                    <Download size={16} className="mr-1" /> PDF
                  </Button>
                  <Button size="sm" variant="secondary" onClick={openDisplayMode}>
                    <Monitor size={16} className="mr-1" /> Display
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setQrDialogOpen(true)}>
                    <QrCode size={16} className="mr-1" /> QR
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Leaderboard Table */}
          {leaderboardData.leaderboard?.length > 0 ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <Target className="text-[#1B3B6F]" />
                  {getSelectedMatchData()?.name} - Rankings
                  <span className="text-sm font-normal text-slate-500 ml-2">
                    ({leaderboardData.leaderboard.length} shooters)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="w-16">Rank</th>
                        <th>Shooter ID</th>
                        <th>Shooter Name</th>
                        <th className="text-center">Best Score</th>
                        <th className="text-center">
                          <span className="flex items-center justify-center gap-1">
                            <Flame size={14} className="text-orange-500" />
                            Longest Run
                          </span>
                        </th>
                        <th className="text-center">Rounds</th>
                        <th className="text-center">Scorecard</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboardData.leaderboard.map((entry) => (
                        <tr key={entry.shooter_id || entry.id} className={getRankClass(entry.rank)}>
                          <td className="font-mono text-lg font-bold text-center">
                            {getRankBadge(entry.rank)}
                          </td>
                          <td className="font-mono text-sm text-slate-500">
                            {entry.shooter_id_number || "-"}
                          </td>
                          <td className="font-medium text-slate-800">
                            {entry.shooter_name}
                          </td>
                          <td className="text-center">
                            <span className="font-mono text-xl font-bold text-green-700">
                              {entry.best_score || 0}
                            </span>
                            <span className="text-slate-400">/{leaderboardData.shots_per_round}</span>
                          </td>
                          <td className="text-center">
                            <span className="font-mono text-lg font-medium text-orange-600">
                              {entry.best_streak || 0}
                            </span>
                          </td>
                          <td className="text-center font-mono text-slate-500">
                            {entry.total_rounds || 0}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => viewScorecard(entry)}
                              data-testid={`view-scorecard-${entry.shooter_id}`}
                            >
                              <Eye size={14} className="mr-1" />
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Trophy className="mx-auto text-slate-300 mb-4" size={48} />
                <p className="text-slate-500">No scores recorded for this match yet.</p>
              </CardContent>
            </Card>
          )}

          {/* Legend */}
          {leaderboardData.leaderboard?.length > 0 && (
            <div className="flex flex-wrap gap-4 justify-center text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded border-l-4 border-yellow-500 bg-yellow-50"></span>
                Gold (1st)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded border-l-4 border-slate-400 bg-slate-50"></span>
                Silver (2nd)
              </span>
              <span className="flex items-center gap-1">
                <span className="w-4 h-4 rounded border-l-4 border-amber-600 bg-amber-50"></span>
                Bronze (3rd)
              </span>
            </div>
          )}
        </>
      )}

      {/* No Match Selected */}
      {!selectedMatch && matches.length > 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Trophy className="mx-auto text-slate-300 mb-4" size={48} />
            <p className="text-slate-500">Click on a match above to view its leaderboard</p>
          </CardContent>
        </Card>
      )}

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-center">
              Scan to View Scores
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4 p-4">
            <div className="bg-white p-4 rounded-lg shadow-inner">
              <QRCodeSVG value={getMobileUrl()} size={256} level="H" includeMargin={true} />
            </div>
            <p className="text-sm text-slate-500 text-center">
              Shooters can scan this QR code to view live scores
            </p>
            <Button onClick={copyMobileLink} variant="outline" className="w-full">
              Copy Link
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scorecard Dialog */}
      <Dialog open={scorecardDialogOpen} onOpenChange={setScorecardDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">
              Best Scorecard - {selectedScorecardShooter?.shooter_name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingScorecard ? (
            <div className="py-8 text-center text-slate-500">Loading scorecard...</div>
          ) : scorecardData && scorecardData.length > 0 ? (
            <div className="space-y-4">
              {scorecardData.map((round, roundIndex) => {
                const scores = round.scores ? (typeof round.scores === 'string' ? JSON.parse(round.scores) : round.scores) : [];
                const totalHits = scores.filter(s => s === true || s === 1).length;
                const totalMisses = scores.length - totalHits;
                const longestRun = calculateLongestRun(scores);
                
                return (
                  <div key={round.id || roundIndex} className="border rounded-lg p-4">
                    {/* Stats Summary */}
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <p className="text-sm text-green-600">Score</p>
                        <p className="font-mono text-2xl font-bold text-green-700">
                          {totalHits}/{scores.length}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <p className="text-sm text-red-600">Misses</p>
                        <p className="font-mono text-2xl font-bold text-red-700">
                          {totalMisses}
                        </p>
                      </div>
                      <div className="text-center p-3 bg-orange-50 rounded-lg">
                        <p className="text-sm text-orange-600">Longest Run</p>
                        <p className="font-mono text-2xl font-bold text-orange-700">
                          {longestRun}
                        </p>
                      </div>
                    </div>
                    
                    {/* Shot Grid */}
                    <p className="text-sm font-medium text-slate-600 mb-2">Shot by Shot</p>
                    <div className="space-y-2">
                      {Array.from({ length: Math.ceil(scores.length / 5) }, (_, rowIndex) => {
                        const startShot = rowIndex * 5;
                        const rowScores = scores.slice(startShot, startShot + 5);
                        
                        return (
                          <div key={rowIndex} className="flex items-center gap-1">
                            <span className="w-12 text-xs text-slate-400">{startShot + 1}-{Math.min(startShot + 5, scores.length)}</span>
                            <div className="flex gap-1">
                              {rowScores.map((score, idx) => (
                                <div
                                  key={idx}
                                  className={`w-8 h-8 rounded flex items-center justify-center text-sm font-bold
                                    ${(score === true || score === 1) 
                                      ? "bg-green-100 text-green-700 border border-green-300" 
                                      : "bg-red-100 text-red-700 border border-red-300"
                                    }`}
                                >
                                  {(score === true || score === 1) ? <Check size={14} /> : <X size={14} />}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Legend */}
                    <div className="flex gap-4 text-xs text-slate-500 mt-3">
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-green-100 border border-green-300 rounded"></div>
                        Hit
                      </span>
                      <span className="flex items-center gap-1">
                        <div className="w-3 h-3 bg-red-100 border border-red-300 rounded"></div>
                        Miss
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-slate-500">No rounds recorded yet</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
